import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

const failed = (error: { message: string; code?: string } | null) => {
  if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
};

export type InboxFilter = "all" | "unread" | "handled" | "opted_out";

export interface Conversation {
  readonly id: string;
  readonly personId: string | null;
  /** The person's name, or "Unknown number" when the sender matches nobody. */
  readonly displayName: string;
  readonly isUnknown: boolean;
  readonly lastBody: string | null;
  readonly lastAt: string | null;
  readonly isUnread: boolean;
  readonly isHandled: boolean;
  readonly isOptedOut: boolean;
  readonly isConfidential: boolean;
  /** A person, agreed to texts, not a child: the database answers them (`inbox_reply` checks it again). */
  readonly canReply: boolean;
}

/**
 * `in`, what they sent; `reply`, a staff answer; `campaign`; `auto`, the website Text us confirmation (ADR 0074); and
 * `web`, a note that a message came through the website's Text us: `body` the page, `status` `consent` or
 * `no_consent`, `reason` the confirmation's outcome (`queued`, or why no text went).
 */
export type ThreadKind = "in" | "reply" | "campaign" | "auto" | "web";

export interface ThreadMessage {
  readonly kind: ThreadKind;
  readonly at: string;
  readonly body: string;
  readonly status: string | null;
  readonly isOptOut: boolean;
  /**
   * Why a text from the church was not sent or not delivered: a held-back answer's reason (`no_consent`, `minor`,
   * `no_person`, `no_connection`, `no_address`), `interrupted`, or the provider's own words. Null otherwise.
   */
  readonly reason: string | null;
}

const inboxKey = (tenantId: string | null) => ["inbox", tenantId] as const;

/** Queued or sent: the answer has not reached its end yet (delivered, failed or not sent). */
export const isUnderway = (status: string | null) => status === "queued" || status === "sent";

/** A page of the list: the database caps a request at 200, so the list asks 50 at a time by offset. */
export const INBOX_PAGE = 50;

type ConversationRow = {
  id: string;
  person_id: string | null;
  display_name: string;
  is_unknown: boolean;
  last_body: string | null;
  last_at: string | null;
  is_unread: boolean;
  is_handled: boolean;
  is_opted_out: boolean;
  is_confidential: boolean;
  can_reply: boolean;
};

export function toConversation(row: ConversationRow): Conversation {
  return {
    id: row.id,
    personId: row.person_id,
    displayName: row.display_name,
    isUnknown: row.is_unknown,
    lastBody: row.last_body,
    lastAt: row.last_at,
    isUnread: row.is_unread,
    isHandled: row.is_handled,
    isOptedOut: row.is_opted_out,
    isConfidential: row.is_confidential,
    canReply: row.can_reply,
  };
}

/**
 * The church's conversations, newest first, a page of 50 at a time (`p_offset`), so every one can be reached however
 * many there are. New texts arrive on their own: the loaded pages are read again every ten seconds (half a minute
 * until 2026-10-14; a visitor texting the church during a conversation waited for the Inbox, not for the carrier).
 */
export function useInboxConversations(filter: InboxFilter) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useInfiniteQuery({
    queryKey: [...inboxKey(tenantId), "list", filter],
    enabled: tenantId !== null,
    refetchInterval: 10_000,
    initialPageParam: 0,
    getNextPageParam: (last: Conversation[], pages: Conversation[][]) =>
      last.length < INBOX_PAGE ? undefined : pages.length * INBOX_PAGE,
    queryFn: async ({ pageParam }): Promise<Conversation[]> => {
      const { data, error } = await supabase.rpc("inbox_conversations", {
        p_tenant_id: tenantId ?? "",
        p_filter: filter,
        p_limit: INBOX_PAGE,
        p_offset: pageParam,
      });
      failed(error);
      return (data ?? []).map(toConversation);
    },
  });
}

/** One conversation's texts in time order: what they sent, and what HoW sent them (campaigns and answers). */
export function useInboxThread(conversationId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...inboxKey(tenantId), "thread", conversationId],
    enabled: tenantId !== null && conversationId !== null,
    // An answer on its way is read again every few seconds, so Sending turns into Sent and Delivered in front of staff;
    // otherwise every ten seconds, so what they text back appears while the conversation is open.
    refetchInterval: (query) =>
      (query.state.data ?? []).some(
        (m) => (m.kind === "reply" || m.kind === "auto") && isUnderway(m.status),
      )
        ? 5_000
        : 10_000,
    queryFn: async (): Promise<ThreadMessage[]> => {
      const { data, error } = await supabase.rpc("inbox_thread", {
        p_conversation_id: conversationId ?? "",
      });
      failed(error);
      return (data ?? []).map((row) => ({
        kind:
          row.kind === "reply" ||
          row.kind === "campaign" ||
          row.kind === "auto" ||
          row.kind === "web"
            ? row.kind
            : "in",
        at: row.at,
        body: row.body,
        status: row.status,
        isOptOut: row.is_opt_out,
        reason: row.reason,
      }));
    },
  });
}

/** How many conversations have a text nobody has opened, for the navigation (0 without the permission). */
export function useInboxUnreadCount(enabled: boolean) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...inboxKey(tenantId), "unread"],
    enabled: enabled && tenantId !== null,
    refetchInterval: 60_000,
    queryFn: async (): Promise<number> => {
      const { data, error } = await supabase.rpc("inbox_unread_count", {
        p_tenant_id: tenantId ?? "",
      });
      failed(error);
      return data ?? 0;
    },
  });
}

function useInboxInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  return async () => {
    await client.invalidateQueries({ queryKey: inboxKey(tenantId) });
  };
}

export function useMarkRead() {
  const supabase = useSupabase();
  const invalidate = useInboxInvalidation();
  return useMutation({
    mutationFn: async (conversationId: string) => {
      const { error } = await supabase.rpc("inbox_mark_read", {
        p_conversation_id: conversationId,
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

export function useSetHandled() {
  const supabase = useSupabase();
  const invalidate = useInboxInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly conversationId: string; readonly handled: boolean }) => {
      const { error } = await supabase.rpc("inbox_set_handled", {
        p_conversation_id: input.conversationId,
        p_handled: input.handled,
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

/** Marks a conversation confidential (D21), or unmarks it; only care.read_confidential unmarks. */
export function useSetConfidential() {
  const supabase = useSupabase();
  const invalidate = useInboxInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly conversationId: string;
      readonly confidential: boolean;
    }) => {
      const { error } = await supabase.rpc("inbox_set_confidential", {
        p_conversation_id: input.conversationId,
        p_confidential: input.confidential,
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}

/** The unknown sender's number, for "Add this person": null once 30 days have passed (D20). Needs people.write. */
export function useUnknownNumber(conversationId: string | null) {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...inboxKey(tenantId), "number", conversationId],
    enabled: conversationId !== null,
    retry: false,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.rpc("inbox_unknown_number", {
        p_conversation_id: conversationId ?? "",
      });
      failed(error);
      return data;
    },
  });
}

/**
 * Why "Add this person" stopped. `personId` is the person already made when a later step failed, so a retry carries
 * on from there instead of making them twice. `alreadyLinked` means a colleague linked the conversation first: the
 * person made here was removed again, so nobody is left over.
 */
export class AddPersonError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
    readonly personId: string | null,
    readonly alreadyLinked: boolean,
  ) {
    super(message);
  }
}

/**
 * "Add this person" from an unknown number: the person, their number as their main phone, then the conversation
 * linked to them (which clears its copy of the number, D20). Three calls, so a failure part-way keeps the person made
 * (`personId` on the error) for the retry, and a conversation someone else linked meanwhile (23505) removes it again.
 * One database function doing all three in a transaction would be sturdier; that is raised with the owner (#353).
 * Consent is not recorded: texting the church is not agreeing to be texted.
 */
export function useAddPersonFromConversation() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  const invalidate = useInboxInvalidation();
  return useMutation({
    mutationFn: async (input: {
      readonly conversationId: string;
      readonly firstName: string;
      readonly lastName: string;
      readonly phone: string | null;
      /** The person a failed attempt already made: the retry starts from them. */
      readonly personId?: string | null;
    }): Promise<string> => {
      let personId = input.personId ?? null;
      const stop = (error: { message: string; code?: string }) =>
        new AddPersonError(error.message, error.code, personId, false);
      if (personId === null) {
        const created = await supabase.rpc("create_person", {
          p_tenant_id: tenantId ?? "",
          p_first_name: input.firstName,
          p_last_name: input.lastName,
        });
        if (created.error) throw stop(created.error);
        personId = created.data;
      }
      if (input.phone !== null) {
        // Saving the same number twice is harmless (the contact is matched on its value), so a retry repeats it.
        const contact = await supabase.rpc("set_person_contact", {
          p_person_id: personId,
          p_method: "phone",
          p_value: input.phone,
          p_is_primary: true,
        });
        if (contact.error) throw stop(contact.error);
      }
      const linked = await supabase.rpc("inbox_link_person", {
        p_conversation_id: input.conversationId,
        p_person_id: personId,
      });
      if (linked.error) {
        if (linked.error.code === "23505") {
          const removed = await supabase.rpc("delete_person", { p_person_id: personId });
          // The person made here could not be removed: say so, and keep them so a retry removes them again.
          if (removed.error)
            throw new AddPersonError(
              "Someone has already added this person, and the record made here could not be removed. Please try again, or delete it from People.",
              removed.error.code,
              personId,
              false,
            );
          throw new AddPersonError(linked.error.message, "23505", null, true);
        }
        throw stop(linked.error);
      }
      return personId;
    },
    onSettled: async () => {
      await invalidate();
      await client.invalidateQueries({ queryKey: ["people", tenantId] });
    },
  });
}

/** Queues an answer with the church's opt-out line; refused in words without consent, to a child or after STOP. */
export function useReply() {
  const supabase = useSupabase();
  const invalidate = useInboxInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly conversationId: string; readonly body: string }) => {
      const { error } = await supabase.rpc("inbox_reply", {
        p_conversation_id: input.conversationId,
        p_body: input.body,
      });
      failed(error);
    },
    onSuccess: invalidate,
  });
}
